/** Configurações › Pontuação e Configurações › Planilha mestre. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { confirmar, toast } from "@/ui/feedback";
import { Badge, Card, DataTable, ErrorBox, Loading, Modal, useAct } from "@/ui/kit";

// =================================================================== pontuação

interface ScoringData {
  latePct: number;
  types: Array<{ id: string; name: string; points: number; active: boolean }>;
  subjects: Array<{ id: string; name: string; area: string; pointsWeight: number; active: boolean }>;
}

export function ScoringSettings(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["pontuacao"], queryFn: () => api.get<ScoringData>("/config/pontuacao") });
  const [late, setLate] = useState(50);
  const [pts, setPts] = useState<Record<string, number>>({});
  const [wts, setWts] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState("");
  useEffect(() => {
    if (!data) return;
    setLate(data.latePct);
    setPts(Object.fromEntries(data.types.map((t) => [t.id, t.points])));
    setWts(Object.fromEntries(data.subjects.map((s) => [s.id, s.pointsWeight])));
  }, [data]);
  const save = useAct(() => api.put("/config/pontuacao", {
    latePct: Math.min(100, Math.max(0, Math.round(late))),
    types: (data?.types ?? []).filter((t) => pts[t.id] !== t.points).map((t) => ({ id: t.id, points: Math.max(0, Math.round(pts[t.id] ?? 0)) })),
    subjects: (data?.subjects ?? []).filter((s) => wts[s.id] !== s.pointsWeight).map((s) => ({ id: s.id, pointsWeight: Math.min(10, Math.max(0, Number(wts[s.id] ?? 1))) })),
  }), [["pontuacao"], ["catalogo"]]);
  if (!data) return <Card title="Pontuação"><ErrorBox error={error} /><Loading /></Card>;
  const q = filter.trim().toLowerCase();
  const subjects = data.subjects.filter((s) => !q || `${s.name} ${s.area}`.toLowerCase().includes(q));
  return (
    <div className="stack gap-16">
      <Card title="Regra de pontuação" actions={<button className="btn primary" disabled={save.isPending} onClick={() => save.mutate()}>Salvar</button>}>
        <div className="stack gap-12 small">
          <div>
            Cada atividade concluída vale <b>pontos do tipo de atividade × peso do tipo de ação</b> do processo/caso.
            Os pontos vão para <b>quem concluiu</b> (o executor, mesmo quando outra pessoa revisa).
            Atividade concluída depois do <b>prazo interno</b> vale só o percentual abaixo.
          </div>
          <label className="row gap-8" style={{ alignItems: "center" }}>
            Fora do prazo interno vale
            <input className="input" type="number" min={0} max={100} value={late} onChange={(e) => setLate(Number(e.target.value))} style={{ width: 90 }} />
            % dos pontos
          </label>
          <div className="muted">Os pontos são gravados no momento da conclusão: mudar a regra não altera o que já foi pontuado. Reabrir uma atividade zera os pontos dela.</div>
        </div>
        <ErrorBox error={save.error} />
      </Card>
      <Card title="Pontos por tipo de atividade">
        <DataTable rows={data.types} rowKey={(t) => t.id} rowClass={(t) => (t.active ? "" : "row-muted")} columns={[
          { key: "n", label: "Tipo", render: (t) => t.name },
          { key: "p", label: "Pontos", width: 140, render: (t) => <input className="input" type="number" min={0} max={1000} value={pts[t.id] ?? 0} onChange={(e) => setPts({ ...pts, [t.id]: Number(e.target.value) })} style={{ width: 100 }} /> },
        ]} />
      </Card>
      <Card title="Peso por tipo de ação" actions={<input className="input" placeholder="Filtrar…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 200 }} />}>
        <div className="muted small" style={{ marginBottom: 8 }}>1 = normal; 2 = vale o dobro; 0,5 = metade. Atividade sem processo/caso usa peso 1.</div>
        <DataTable rows={subjects} rowKey={(s) => s.id} rowClass={(s) => (s.active ? "" : "row-muted")} columns={[
          { key: "a", label: "Área", width: 180, render: (s) => s.area },
          { key: "n", label: "Tipo de ação", render: (s) => s.name },
          { key: "w", label: "Peso", width: 140, render: (s) => <input className="input" type="number" min={0} max={10} step={0.1} value={wts[s.id] ?? 1} onChange={(e) => setWts({ ...wts, [s.id]: Number(e.target.value) })} style={{ width: 100 }} /> },
        ]} />
      </Card>
    </div>
  );
}

// =================================================================== planilha mestre

interface RegistryInfo { key: string; label: string; group: string; fields: string[] }
interface DiffT {
  registry: string;
  creates: Array<{ line: number; values: Record<string, unknown> }>;
  updates: Array<{ line: number; id: string; label: string; changes: Array<{ field: string; from: unknown; to: unknown }> }>;
  errors: Array<{ line: number; message: string }>;
  warnings: string[];
  unchanged: number; totalCreates: number; totalUpdates: number;
}

const show = (v: unknown): string => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "sim" : "não") : String(v));

export function MasterSheets(): JSX.Element {
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ["cadastros"], queryFn: () => api.get<RegistryInfo[]>("/cadastros") });
  const [sel, setSel] = useState<string | null>(null);
  const [prev, setPrev] = useState<{ token: string; diff: DiffT } | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const groups = useMemo(() => {
    const m = new Map<string, RegistryInfo[]>();
    for (const r of data ?? []) m.set(r.group, [...(m.get(r.group) ?? []), r]);
    return [...m.entries()];
  }, [data]);
  const reg = (data ?? []).find((r) => r.key === sel) ?? null;
  const send = useAct(async (f: File) => { const fd = new FormData(); fd.append("file", f); return api.upload<{ token: string; diff: DiffT }>(`/cadastros/${sel}/previa`, fd); }, [], (out) => setPrev(out), false);
  const apply = useAct(async () => api.post<{ created: number; updated: number }>(`/cadastros/${sel}/aplicar`, { token: prev!.token }), [], (out) => {
    toast.ok(`Planilha aplicada: ${out.created} novo(s), ${out.updated} alterado(s).`);
    setPrev(null); send.reset();
    void qc.invalidateQueries();
  }, false);
  const download = async (r: RegistryInfo): Promise<void> => {
    try { await api.download(`/cadastros/${r.key}/planilha`, `VG-${r.label}.xlsx`); } catch (e) { toast.error((e as Error).message || "Falha ao baixar"); }
  };

  if (!data) return <Card title="Planilha mestre"><ErrorBox error={error} /><Loading /></Card>;
  const d = prev?.diff;
  return (
    <div className="stack gap-16">
      <Card title="Planilha mestre">
        <div className="stack gap-12 small">
          <div>
            Para alterar muitos cadastros de uma vez: <b>baixe</b> a planilha do cadastro, altere no Excel (ou acrescente linhas novas com a coluna
            “codigo” vazia) e <b>envie</b>. Antes de gravar, o sistema mostra exatamente o que vai mudar; só depois de <b>Aplicar</b> as mudanças entram,
            todas juntas (se algo falhar, nada é gravado). Nada é apagado: para desativar, use “não” na coluna “ativo”.
            <br />A planilha vem protegida: colunas como área, unidade, responsável, fase, situação, instância, sistema e etapa só aceitam
            itens da lista suspensa (o que está cadastrado no sistema), e a coluna “codigo” fica travada. Precisa de um item novo? Cadastre no sistema e baixe de novo.
          </div>
          {groups.map(([g, list]) => (
            <div key={g} className="stack gap-6">
              <div className="label">{g}</div>
              <div className="master-grid">
                {list.map((r) => (
                  <div key={r.key} className={`master-item${sel === r.key ? " on" : ""}`}>
                    <div><b>{r.label}</b><div className="muted tiny">{r.fields.slice(0, 6).join(", ")}{r.fields.length > 6 ? "…" : ""}</div></div>
                    <div className="row gap-6">
                      <button className="btn sm" onClick={() => void download(r)}>Baixar</button>
                      <button className="btn sm primary" disabled={send.isPending} onClick={() => { setSel(r.key); setPrev(null); setTimeout(() => file.current?.click(), 0); }}>Enviar</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <input ref={file} type="file" accept=".xlsx" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f && sel) send.mutate(f); }} />
        </div>
      </Card>

      {reg && (send.isPending || send.error || d) && (
        <Modal wide="xl" title={`Planilha mestre: ${reg.label}`} onClose={() => { if (!apply.isPending) { setPrev(null); send.reset(); } }}>
          {send.isPending ? <div className="stack gap-8"><div className="info-box">Lendo a planilha e comparando com o sistema…</div><Loading /></div>
            : send.error ? <div className="stack gap-8"><ErrorBox error={send.error} /><div className="row" style={{ justifyContent: "flex-end" }}><button className="btn" onClick={() => send.reset()}>Fechar</button></div></div>
            : d && (
          <div className="stack gap-12 small">
            <div className="row gap-8">
              <Badge tone="info">{d.totalCreates} novo(s)</Badge>
              <Badge tone="warn">{d.totalUpdates} alterado(s)</Badge>
              <Badge tone="muted">{d.unchanged} sem mudança</Badge>
              {d.errors.length > 0 ? <Badge tone="bad">{d.errors.length} erro(s) — nada será gravado</Badge> : <Badge tone="ok">sem erros</Badge>}
            </div>
            <div className="muted">Nada foi gravado ainda. Confira abaixo e clique em <b>Aplicar</b> para gravar tudo junto (se algo falhar, nada é gravado).</div>
            {d.errors.length > 0 && (
              <div className="error-box">
                <b>Corrija na planilha e envie de novo:</b>
                <ul>{d.errors.slice(0, 100).map((e, i) => <li key={i}>Linha {e.line}: {e.message}</li>)}</ul>
                {d.errors.length > 100 && <div>… e mais {d.errors.length - 100}.</div>}
              </div>
            )}
            {d.warnings.length > 0 && <div className="warn-box"><ul>{d.warnings.slice(0, 50).map((w, i) => <li key={i}>{w}</li>)}</ul></div>}
            {d.updates.length > 0 && (
              <div className="stack gap-6">
                <div className="label">Alterações</div>
                <DataTable rows={d.updates} rowKey={(u) => `${u.id}-${u.line}`} columns={[
                  { key: "l", label: "Linha", width: 70, render: (u) => u.line },
                  { key: "r", label: "Registro", width: 220, render: (u) => u.label },
                  { key: "c", label: "O que muda", render: (u) => <div className="stack gap-2">{u.changes.map((c, i) => <div key={i}><b>{c.field}</b>: <span className="muted strike">{show(c.from)}</span> → {show(c.to)}</div>)}</div> },
                ]} />
              </div>
            )}
            {d.creates.length > 0 && (
              <div className="stack gap-6">
                <div className="label">Novos</div>
                <DataTable rows={d.creates} rowKey={(c) => String(c.line)} columns={[
                  { key: "l", label: "Linha", width: 70, render: (c) => c.line },
                  { key: "v", label: "Dados", render: (c) => Object.entries(c.values).filter(([, v]) => v !== null && v !== "").map(([k, v]) => `${k}: ${show(v)}`).join(" · ") },
                ]} />
              </div>
            )}
            {!d.updates.length && !d.creates.length && !d.errors.length && <div className="muted">Nada mudou em relação ao que está no sistema.</div>}
            <ErrorBox error={apply.error} />
            <div className="row gap-8" style={{ justifyContent: "flex-end" }}>
              <button className="btn" disabled={apply.isPending} onClick={() => { setPrev(null); send.reset(); }}>Descartar</button>
              <button className="btn primary" disabled={apply.isPending || d.errors.length > 0 || (!d.totalCreates && !d.totalUpdates)}
                onClick={async () => { if (await confirmar(`Aplicar ${d.totalCreates} novo(s) e ${d.totalUpdates} alteração(ões) em ${reg.label}?`, { confirm: "Aplicar" })) apply.mutate(); }}>
                {apply.isPending ? "Aplicando…" : "Aplicar"}
              </button>
            </div>
          </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// =================================================================== ranking (Indicadores e Início)

export interface RankRow { userId: string; name: string; points: number; done: number; late: number; onTime: number }

export function ScoreRanking({ from, to, unitId, compact }: { from?: string; to?: string; unitId?: string | null; compact?: boolean }): JSX.Element {
  const q = new URLSearchParams();
  if (from) q.set("de", from);
  if (to) q.set("ate", to);
  if (unitId) q.set("unidade", unitId);
  const { data, error } = useQuery({ queryKey: ["bi-pontuacao", from, to, unitId], queryFn: () => api.get<{ team: boolean; rows: RankRow[] }>(`/bi/pontuacao?${q.toString()}`) });
  if (!data) return <><ErrorBox error={error} /><Loading /></>;
  const max = Math.max(1, ...data.rows.map((r) => r.points));
  const rows = compact ? data.rows.slice(0, 8) : data.rows;
  if (!rows.length) return <div className="muted small">Nenhuma atividade pontuada no período.</div>;
  return (
    <div className="rank-list">
      {rows.map((r, i) => (
        <div key={r.userId} className="rank-row">
          <span className="rank-pos">{i + 1}º</span>
          <span className="rank-name" title={r.name}>{r.name}</span>
          <span className="rank-bar"><span style={{ width: `${(r.points / max) * 100}%` }} /></span>
          <b className="rank-pts">{r.points.toLocaleString("pt-BR")}</b>
          {!compact && <span className="muted tiny rank-meta">{r.done} concluída(s) · {r.onTime} no prazo · {r.late} fora</span>}
        </div>
      ))}
    </div>
  );
}
